'use client';

import { c as _c } from "react/compiler-runtime";
import { shallow, useCreateAtom, useSelector } from '@repo/tanstack/store';
import { functionalUpdate } from '@tanstack/react-table';
import { type Dispatch, type ReactNode, type SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isAddedAfterRollout, isMarkedNew } from '../column-meta';
import { useTable } from '../tanstack-table';
import type { ColumnDef, ColumnOrderState, ColumnSizingState, PersistedColumnPinningState, RowData, SortingState, Table, TableColumnPinningState, TableFeatures, TableState } from '../tanstack-table';
import { tableFeatures, toTableColumnPinning } from '../tanstack-table';
import { type ColumnSettingDefs, type ColumnSettings, DEFAULT_ITEMS_SHOWN, isDisplayOption, type ItemsShownCount, NO_COLUMN_SETTINGS, selectFields, withDisplayChoice, withItemsShown } from './merge-table-settings';
import { useTableFilters } from './use-table-filters.ts';
import { useTableSorting } from './use-table-sorting.ts';
import type { TableColumnOwner } from './table-column-owner';
import { useTableUserSettings } from './use-table-user-settings.ts';
import { type ITableStateContext, type TableBulkActionsOptions, TableStateContext, type TableStateContextValue } from './table-state-context';
import { createSelectionColumn, SELECTION_COLUMN_ID } from '../components/selection-column';
const EMPTY_COLUMN_PINNING: PersistedColumnPinningState = {};
const EMPTY_COLUMN_VISIBILITY: Record<string, boolean> = {};
const SELECTION_COLUMN_IDS = [SELECTION_COLUMN_ID];
const SELECTION_REQUIRED_PINNING: PersistedColumnPinningState = {
  left: SELECTION_COLUMN_IDS
};
const SELECTION_REQUIRED_SIZING: ColumnSizingState = {
  [SELECTION_COLUMN_ID]: 48
};
const SELECTION_REQUIRED_VISIBILITY = {
  [SELECTION_COLUMN_ID]: true
};
type DerivedColumnState = {
  columnSettingDefs: ColumnSettingDefs;
  initialColumnOrder: string[];
  initialColumnSizing: ColumnSizingState;
  lockedColumnPinning: PersistedColumnPinningState;
  /** The Column fields table settings mark New, for every column with a Field selection. */
  newFieldIds: Record<string, ReadonlySet<string>>;
  postRolloutColumnIds: string[];
  sorts: string[];
};
const resolveStateUpdate = <T,>(updater: SetStateAction<T>, current: T): T => functionalUpdate(updater, current);
const createTableStateView = <TData extends RowData, TSelected extends object>(table: Table<TData, object>, state: TSelected, bulkActionsEnabled = false): Table<TData, TSelected> =>
// SAFETY: Table<TData, TSelected> differs from Table<TData, object> only in `state`, and the get
// trap returns the selected `state`; the other overrides keep the setters' signatures.
new Proxy(table, {
  get(target, property, receiver) {
    if (property === 'state') return state;
    if (!bulkActionsEnabled) return Reflect.get(target, property, receiver);
    if (property === 'setColumnOrder') {
      return (updater: SetStateAction<ColumnOrderState>) => target.setColumnOrder(current => {
        const next = resolveStateUpdate(updater, current);
        return [SELECTION_COLUMN_ID, ...next.filter(id => id !== SELECTION_COLUMN_ID)];
      });
    }
    if (property === 'setColumnPinning') {
      return (updater: SetStateAction<TableColumnPinningState>) => target.setColumnPinning(current => {
        const next = resolveStateUpdate(updater, current);
        return {
          end: next.end.filter(id => id !== SELECTION_COLUMN_ID),
          start: [SELECTION_COLUMN_ID, ...next.start.filter(id => id !== SELECTION_COLUMN_ID)]
        };
      });
    }
    if (property === 'setColumnVisibility') {
      return (updater: SetStateAction<Record<string, boolean>>) => target.setColumnVisibility(current => ({
        ...resolveStateUpdate(updater, current),
        [SELECTION_COLUMN_ID]: true
      }));
    }
    if (property === 'setColumnSizing') {
      return (updater: SetStateAction<ColumnSizingState>) => target.setColumnSizing(current => ({
        ...resolveStateUpdate(updater, current),
        [SELECTION_COLUMN_ID]: 48
      }));
    }
    return Reflect.get(target, property, receiver);
  }
}) as Table<TData, TSelected>;
export const TableStateProvider = t0 => {
  const $ = _c(163);
  const {
    bulkActions,
    children,
    columnOrderVersion,
    columnOwner,
    columns,
    initialColumnPinning: t1,
    initialColumnVisibility: t2,
    knownColumnsRollout,
    onSortingChange
  } = t0;
  const initialColumnPinning = t1 === undefined ? EMPTY_COLUMN_PINNING : t1;
  const initialColumnVisibility = t2 === undefined ? EMPTY_COLUMN_VISIBILITY : t2;
  let state;
  if ($[0] !== columns || $[1] !== knownColumnsRollout) {
    state = {
      columnSettingDefs: {
        columnFields: {},
        displayChoices: {},
        listCells: {}
      },
      initialColumnOrder: [],
      initialColumnSizing: {},
      lockedColumnPinning: {
        left: [],
        right: []
      },
      newFieldIds: {},
      postRolloutColumnIds: [],
      sorts: []
    };
    const visitColumns = definitions => {
      for (const column of definitions) {
        const childColumns = "columns" in column ? column.columns : undefined;
        const hasChildren = childColumns !== undefined && childColumns.length > 0;
        const columnId = column.id;
        const missingId = !columnId;
        bb23: switch (true) {
          case hasChildren:
            {
              visitColumns(childColumns);
              break bb23;
            }
          case missingId:
            {
              break bb23;
            }
          default:
            {
              state.initialColumnOrder.push(columnId);
              if (column.size !== undefined) {
                state.initialColumnSizing[columnId] = column.size;
              }
              if (column.meta?.lockedPinning === "start") {
                state.lockedColumnPinning.left?.push(columnId);
              }
              if (column.meta?.lockedPinning === "end") {
                state.lockedColumnPinning.right?.push(columnId);
              }
              if (column.enableSorting) {
                state.sorts.push(columnId);
              }
              if (column.meta?.fields && column.meta.fields.length > 1) {
                state.columnSettingDefs.columnFields[columnId] = column.meta.fields;
                state.newFieldIds[columnId] = new Set(column.meta.fields.filter(field => isMarkedNew(field.addedOn, knownColumnsRollout)).map(_temp));
              }
              if (column.meta?.displayChoices?.length) {
                state.columnSettingDefs.displayChoices[columnId] = column.meta.displayChoices;
              }
              if (column.meta?.listCell) {
                state.columnSettingDefs.listCells[columnId] = column.meta.listCell === true ? DEFAULT_ITEMS_SHOWN : column.meta.listCell.defaultItemsShown;
              }
              if (isAddedAfterRollout(column.meta?.addedOn, knownColumnsRollout)) {
                state.postRolloutColumnIds.push(columnId);
              }
            }
        }
      }
    };
    visitColumns(columns);
    $[0] = columns;
    $[1] = knownColumnsRollout;
    $[2] = state;
  } else {
    state = $[2];
  }
  const derivedState = state;
  const resolvedInitialColumnOrder = derivedState.initialColumnOrder;
  const resolvedInitialColumnSizing = derivedState.initialColumnSizing;
  const resolvedLockedColumnPinning = derivedState.lockedColumnPinning;
  const resolvedSorts = derivedState.sorts;
  let t3;
  if ($[3] !== initialColumnPinning) {
    t3 = toTableColumnPinning(initialColumnPinning);
    $[3] = initialColumnPinning;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const resolvedInitialColumnPinning = t3;
  let t4;
  if ($[5] !== bulkActions || $[6] !== resolvedInitialColumnOrder) {
    t4 = bulkActions ? [SELECTION_COLUMN_ID, ...resolvedInitialColumnOrder.filter(_temp2)] : resolvedInitialColumnOrder;
    $[5] = bulkActions;
    $[6] = resolvedInitialColumnOrder;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const tableInitialColumnOrder = t4;
  let t5;
  if ($[8] !== bulkActions || $[9] !== columns) {
    t5 = bulkActions ? [createSelectionColumn(), ...columns] : columns;
    $[8] = bulkActions;
    $[9] = columns;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const tableColumns = t5;
  let t6;
  if ($[11] !== bulkActions || $[12] !== resolvedInitialColumnPinning) {
    t6 = bulkActions ? {
      end: resolvedInitialColumnPinning.end.filter(_temp3),
      start: [SELECTION_COLUMN_ID, ...resolvedInitialColumnPinning.start.filter(_temp4)]
    } : resolvedInitialColumnPinning;
    $[11] = bulkActions;
    $[12] = resolvedInitialColumnPinning;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const tableInitialColumnPinning = t6;
  let t7;
  if ($[14] !== bulkActions || $[15] !== resolvedInitialColumnSizing) {
    t7 = bulkActions ? {
      ...resolvedInitialColumnSizing,
      [SELECTION_COLUMN_ID]: 48
    } : resolvedInitialColumnSizing;
    $[14] = bulkActions;
    $[15] = resolvedInitialColumnSizing;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const tableInitialColumnSizing = t7;
  let t8;
  if ($[17] !== bulkActions || $[18] !== initialColumnVisibility) {
    t8 = bulkActions ? {
      ...initialColumnVisibility,
      [SELECTION_COLUMN_ID]: true
    } : initialColumnVisibility;
    $[17] = bulkActions;
    $[18] = initialColumnVisibility;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const tableInitialColumnVisibility = t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const [data, setData] = useState(t9);
  const tableRef = useRef(null);
  const {
    filters,
    setFilters
  } = useTableFilters();
  let t10;
  if ($[21] !== onSortingChange || $[22] !== resolvedSorts) {
    t10 = {
      onSortingChange,
      sorts: resolvedSorts
    };
    $[21] = onSortingChange;
    $[22] = resolvedSorts;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const {
    isSortPending,
    setSorting,
    sorting
  } = useTableSorting(t10);
  const columnOrder = useCreateAtom(tableInitialColumnOrder);
  const columnPinning = useCreateAtom(tableInitialColumnPinning);
  const columnSizing = useCreateAtom(tableInitialColumnSizing);
  const columnVisibility = useCreateAtom(tableInitialColumnVisibility);
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {};
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const rowSelection = useCreateAtom(t11);
  let t12;
  if ($[25] !== derivedState.columnSettingDefs.columnFields) {
    t12 = {
      ...NO_COLUMN_SETTINGS,
      fields: Object.fromEntries(Object.entries(derivedState.columnSettingDefs.columnFields).map(_temp6))
    };
    $[25] = derivedState.columnSettingDefs.columnFields;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const columnSettings = useCreateAtom(t12);
  const selectionScopeKeyRef = useRef(bulkActions?.selectionScopeKey);
  let t13;
  if ($[27] !== bulkActions?.selectionScopeKey || $[28] !== rowSelection) {
    t13 = () => {
      const selectionScopeKey = bulkActions?.selectionScopeKey;
      if (selectionScopeKey === selectionScopeKeyRef.current) {
        return;
      }
      selectionScopeKeyRef.current = selectionScopeKey;
      rowSelection.set({});
    };
    $[27] = bulkActions?.selectionScopeKey;
    $[28] = rowSelection;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = bulkActions?.selectionScopeKey;
  let t15;
  if ($[30] !== rowSelection || $[31] !== t14) {
    t15 = [t14, rowSelection];
    $[30] = rowSelection;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  useEffect(t13, t15);
  let t16;
  if ($[33] !== bulkActions) {
    t16 = bulkActions ? {
      enableMultiRowSelection: true,
      enableRowRangeSelection: false,
      enableSubRowSelection: false,
      getRowId: bulkActions.getRowId
    } : {
      enableMultiRowSelection: false,
      enableRowRangeSelection: false,
      enableSubRowSelection: false
    };
    $[33] = bulkActions;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const selectionOptions = t16;
  let t17;
  if ($[35] !== columnOrder || $[36] !== columnPinning || $[37] !== columnSizing || $[38] !== columnVisibility || $[39] !== rowSelection) {
    t17 = {
      columnOrder,
      columnPinning,
      columnSizing,
      columnVisibility,
      rowSelection
    };
    $[35] = columnOrder;
    $[36] = columnPinning;
    $[37] = columnSizing;
    $[38] = columnVisibility;
    $[39] = rowSelection;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      enableColumnFilter: false,
      enableHiding: true,
      enableResizing: true,
      maxSize: 500,
      minSize: 60,
      size: 150
    };
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = Boolean(bulkActions);
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {};
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== tableInitialColumnOrder || $[44] !== tableInitialColumnPinning || $[45] !== tableInitialColumnSizing || $[46] !== tableInitialColumnVisibility) {
    t21 = {
      columnOrder: tableInitialColumnOrder,
      columnPinning: tableInitialColumnPinning,
      columnSizing: tableInitialColumnSizing,
      columnVisibility: tableInitialColumnVisibility,
      rowSelection: t20
    };
    $[43] = tableInitialColumnOrder;
    $[44] = tableInitialColumnPinning;
    $[45] = tableInitialColumnSizing;
    $[46] = tableInitialColumnVisibility;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== bulkActions || $[49] !== columnOrder) {
    t22 = updater => columnOrder.set(current => {
      const next = resolveStateUpdate(updater, current);
      return bulkActions ? [SELECTION_COLUMN_ID, ...next.filter(_temp7)] : next;
    });
    $[48] = bulkActions;
    $[49] = columnOrder;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== bulkActions || $[52] !== columnPinning) {
    t23 = updater_0 => columnPinning.set(current_0 => {
      const next_0 = resolveStateUpdate(updater_0, current_0);
      return bulkActions ? {
        end: next_0.end.filter(_temp8),
        start: [SELECTION_COLUMN_ID, ...next_0.start.filter(_temp9)]
      } : next_0;
    });
    $[51] = bulkActions;
    $[52] = columnPinning;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== bulkActions || $[55] !== columnSizing) {
    t24 = updater_1 => columnSizing.set(current_1 => ({
      ...resolveStateUpdate(updater_1, current_1),
      ...(bulkActions ? {
        [SELECTION_COLUMN_ID]: 48
      } : {})
    }));
    $[54] = bulkActions;
    $[55] = columnSizing;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== bulkActions || $[58] !== columnVisibility) {
    t25 = updater_2 => columnVisibility.set(current_2 => ({
      ...resolveStateUpdate(updater_2, current_2),
      ...(bulkActions ? {
        [SELECTION_COLUMN_ID]: true
      } : {})
    }));
    $[57] = bulkActions;
    $[58] = columnVisibility;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== filters || $[61] !== sorting) {
    t26 = {
      columnFilters: filters,
      sorting
    };
    $[60] = filters;
    $[61] = sorting;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== data || $[64] !== selectionOptions || $[65] !== setFilters || $[66] !== setSorting || $[67] !== t17 || $[68] !== t19 || $[69] !== t21 || $[70] !== t22 || $[71] !== t23 || $[72] !== t24 || $[73] !== t25 || $[74] !== t26 || $[75] !== tableColumns) {
    t27 = {
      atoms: t17,
      autoResetCellSelection: false,
      columnResizeMode: "onChange",
      columns: tableColumns,
      data,
      defaultColumn: t18,
      enableColumnPinning: true,
      enableColumnResizing: true,
      enableMultiSort: false,
      enableRowSelection: t19,
      ...selectionOptions,
      features: tableFeatures,
      initialState: t21,
      manualExpanding: true,
      manualPagination: true,
      manualSorting: true,
      onColumnFiltersChange: setFilters,
      onColumnOrderChange: t22,
      onColumnPinningChange: t23,
      onColumnSizingChange: t24,
      onColumnVisibilityChange: t25,
      onSortingChange: setSorting,
      state: t26
    };
    $[63] = data;
    $[64] = selectionOptions;
    $[65] = setFilters;
    $[66] = setSorting;
    $[67] = t17;
    $[68] = t19;
    $[69] = t21;
    $[70] = t22;
    $[71] = t23;
    $[72] = t24;
    $[73] = t25;
    $[74] = t26;
    $[75] = tableColumns;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  const table = useTable(t27, _temp10);
  const t28 = bulkActions ? SELECTION_COLUMN_IDS : undefined;
  const t29 = bulkActions ? SELECTION_COLUMN_IDS : undefined;
  const t30 = bulkActions ? SELECTION_REQUIRED_PINNING : undefined;
  const t31 = bulkActions ? SELECTION_REQUIRED_SIZING : undefined;
  const t32 = bulkActions ? SELECTION_REQUIRED_VISIBILITY : undefined;
  let t33;
  if ($[77] !== columnOrderVersion || $[78] !== columnOwner || $[79] !== columnSettings || $[80] !== derivedState.columnSettingDefs || $[81] !== derivedState.postRolloutColumnIds || $[82] !== initialColumnPinning || $[83] !== initialColumnVisibility || $[84] !== knownColumnsRollout || $[85] !== resolvedInitialColumnOrder || $[86] !== resolvedInitialColumnSizing || $[87] !== resolvedLockedColumnPinning || $[88] !== t28 || $[89] !== t29 || $[90] !== t30 || $[91] !== t31 || $[92] !== t32 || $[93] !== table) {
    t33 = {
      columnOrderVersion,
      columnOwner,
      columnSettingDefs: derivedState.columnSettingDefs,
      columnSettings,
      initialColumnOrder: resolvedInitialColumnOrder,
      initialColumnPinning,
      initialColumnSizing: resolvedInitialColumnSizing,
      initialColumnVisibility,
      knownColumnsRollout,
      lockedColumnPinning: resolvedLockedColumnPinning,
      nonPersistedColumnIds: t28,
      postRolloutColumnIds: derivedState.postRolloutColumnIds,
      requiredColumnOrder: t29,
      requiredColumnPinning: t30,
      requiredColumnSizing: t31,
      requiredColumnVisibility: t32,
      table
    };
    $[77] = columnOrderVersion;
    $[78] = columnOwner;
    $[79] = columnSettings;
    $[80] = derivedState.columnSettingDefs;
    $[81] = derivedState.postRolloutColumnIds;
    $[82] = initialColumnPinning;
    $[83] = initialColumnVisibility;
    $[84] = knownColumnsRollout;
    $[85] = resolvedInitialColumnOrder;
    $[86] = resolvedInitialColumnSizing;
    $[87] = resolvedLockedColumnPinning;
    $[88] = t28;
    $[89] = t29;
    $[90] = t30;
    $[91] = t31;
    $[92] = t32;
    $[93] = table;
    $[94] = t33;
  } else {
    t33 = $[94];
  }
  const userSettings = useTableUserSettings(t33);
  let t34;
  if ($[95] !== derivedState.newFieldIds) {
    t34 = visibility => Object.entries(derivedState.newFieldIds).some(t35 => {
      const [id_3, fieldIds] = t35;
      return fieldIds.size > 0 && visibility[id_3] !== false;
    });
    $[95] = derivedState.newFieldIds;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  const hasNewShownField = useSelector(columnVisibility, t34);
  let t35;
  if ($[97] !== bulkActions || $[98] !== table) {
    t35 = updater_3 => table.setColumnOrder(current_3 => {
      const next_1 = resolveStateUpdate(updater_3, current_3);
      return bulkActions ? [SELECTION_COLUMN_ID, ...next_1.filter(_temp11)] : next_1;
    });
    $[97] = bulkActions;
    $[98] = table;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  const setColumnOrder = t35;
  let t36;
  if ($[100] !== bulkActions || $[101] !== table) {
    t36 = updater_4 => table.setColumnPinning(current_4 => {
      const next_2 = resolveStateUpdate(updater_4, current_4);
      return bulkActions ? {
        end: next_2.end.filter(_temp12),
        start: [SELECTION_COLUMN_ID, ...next_2.start.filter(_temp13)]
      } : next_2;
    });
    $[100] = bulkActions;
    $[101] = table;
    $[102] = t36;
  } else {
    t36 = $[102];
  }
  const setColumnPinning = t36;
  let t37;
  if ($[103] !== bulkActions || $[104] !== table) {
    t37 = updater_5 => table.setColumnSizing(current_5 => ({
      ...resolveStateUpdate(updater_5, current_5),
      ...(bulkActions ? {
        [SELECTION_COLUMN_ID]: 48
      } : {})
    }));
    $[103] = bulkActions;
    $[104] = table;
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  const setColumnSizing = t37;
  let t38;
  if ($[106] !== bulkActions || $[107] !== table) {
    t38 = updater_6 => table.setColumnVisibility(current_6 => ({
      ...resolveStateUpdate(updater_6, current_6),
      ...(bulkActions ? {
        [SELECTION_COLUMN_ID]: true
      } : {})
    }));
    $[106] = bulkActions;
    $[107] = table;
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  const setColumnVisibility = t38;
  let t39;
  if ($[109] !== columnSettings || $[110] !== derivedState.columnSettingDefs.columnFields) {
    t39 = (columnId_3, fieldIds_0) => {
      const fields_0 = derivedState.columnSettingDefs.columnFields[columnId_3];
      if (!fields_0) {
        return;
      }
      columnSettings.set(current_7 => ({
        ...current_7,
        fields: {
          ...current_7.fields,
          [columnId_3]: selectFields(fields_0, fieldIds_0)
        }
      }));
    };
    $[109] = columnSettings;
    $[110] = derivedState.columnSettingDefs.columnFields;
    $[111] = t39;
  } else {
    t39 = $[111];
  }
  const setFieldSelection = t39;
  let t40;
  if ($[112] !== columnSettings || $[113] !== derivedState.columnSettingDefs.listCells) {
    t40 = (columnId_4, count) => {
      const defaultCount = derivedState.columnSettingDefs.listCells[columnId_4];
      if (defaultCount === undefined) {
        return;
      }
      columnSettings.set(current_8 => ({
        ...current_8,
        itemsShown: withItemsShown(current_8.itemsShown, columnId_4, count, defaultCount)
      }));
    };
    $[112] = columnSettings;
    $[113] = derivedState.columnSettingDefs.listCells;
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  const setItemsShown = t40;
  let t41;
  if ($[115] !== columnSettings || $[116] !== derivedState.columnSettingDefs.displayChoices) {
    t41 = (columnId_5, choiceId, value) => {
      const choice = derivedState.columnSettingDefs.displayChoices[columnId_5]?.find(t42 => {
        const {
          id: id_7
        } = t42;
        return id_7 === choiceId;
      });
      if (!choice || !isDisplayOption(choice, value)) {
        return;
      }
      columnSettings.set(current_9 => ({
        ...current_9,
        display: withDisplayChoice(current_9.display, columnId_5, choice, value)
      }));
    };
    $[115] = columnSettings;
    $[116] = derivedState.columnSettingDefs.displayChoices;
    $[117] = t41;
  } else {
    t41 = $[117];
  }
  const setDisplayChoice = t41;
  let t42;
  if ($[118] !== columns || $[119] !== knownColumnsRollout) {
    let t43;
    if ($[121] !== knownColumnsRollout) {
      t43 = column_0 => column_0.id && isMarkedNew(column_0.meta?.addedOn, knownColumnsRollout) ? [column_0.id] : [];
      $[121] = knownColumnsRollout;
      $[122] = t43;
    } else {
      t43 = $[122];
    }
    t42 = new Set(columns.flatMap(t43));
    $[118] = columns;
    $[119] = knownColumnsRollout;
    $[120] = t42;
  } else {
    t42 = $[120];
  }
  const newColumnIds = t42;
  let t43;
  if ($[123] !== columnOrder) {
    t43 = columnOrder.get();
    $[123] = columnOrder;
    $[124] = t43;
  } else {
    t43 = $[124];
  }
  let t44;
  if ($[125] !== columnPinning) {
    t44 = columnPinning.get();
    $[125] = columnPinning;
    $[126] = t44;
  } else {
    t44 = $[126];
  }
  const t45 = derivedState.columnSettingDefs;
  let t46;
  if ($[127] !== columnSizing) {
    t46 = columnSizing.get();
    $[127] = columnSizing;
    $[128] = t46;
  } else {
    t46 = $[128];
  }
  let t47;
  if ($[129] !== columnVisibility) {
    t47 = columnVisibility.get();
    $[129] = columnVisibility;
    $[130] = t47;
  } else {
    t47 = $[130];
  }
  const t48 = newColumnIds.size > 0 || hasNewShownField;
  let t49;
  if ($[131] !== bulkActions || $[132] !== columnSettings || $[133] !== columns || $[134] !== data.length || $[135] !== derivedState.columnSettingDefs || $[136] !== derivedState.newFieldIds || $[137] !== isSortPending || $[138] !== newColumnIds || $[139] !== resolvedInitialColumnOrder || $[140] !== resolvedSorts || $[141] !== setColumnOrder || $[142] !== setColumnPinning || $[143] !== setColumnSizing || $[144] !== setColumnVisibility || $[145] !== setDisplayChoice || $[146] !== setFieldSelection || $[147] !== setItemsShown || $[148] !== setSorting || $[149] !== sorting || $[150] !== t43 || $[151] !== t44 || $[152] !== t46 || $[153] !== t47 || $[154] !== t48 || $[155] !== table || $[156] !== userSettings.resetSettings || $[157] !== userSettings.retrySave || $[158] !== userSettings.saveStatus) {
    t49 = {
      bulkActions,
      columnOrder: t43,
      columnPinning: t44,
      columns,
      columnSettingDefs: t45,
      columnSettings,
      columnSizing: t46,
      columnVisibility: t47,
      hasNewSettings: t48,
      initialColumnOrder: resolvedInitialColumnOrder,
      isSortPending,
      loadedRowCount: data.length,
      newColumnIds,
      newFieldIds: derivedState.newFieldIds,
      onSortingChange: setSorting,
      resetSettings: userSettings.resetSettings,
      retrySave: userSettings.retrySave,
      saveStatus: userSettings.saveStatus,
      setColumnOrder,
      setColumnPinning,
      setColumnSizing,
      setColumnVisibility,
      setData,
      setDisplayChoice,
      setFieldSelection,
      setItemsShown,
      setSorting,
      sorting,
      sorts: resolvedSorts,
      table,
      tableRef
    };
    $[131] = bulkActions;
    $[132] = columnSettings;
    $[133] = columns;
    $[134] = data.length;
    $[135] = derivedState.columnSettingDefs;
    $[136] = derivedState.newFieldIds;
    $[137] = isSortPending;
    $[138] = newColumnIds;
    $[139] = resolvedInitialColumnOrder;
    $[140] = resolvedSorts;
    $[141] = setColumnOrder;
    $[142] = setColumnPinning;
    $[143] = setColumnSizing;
    $[144] = setColumnVisibility;
    $[145] = setDisplayChoice;
    $[146] = setFieldSelection;
    $[147] = setItemsShown;
    $[148] = setSorting;
    $[149] = sorting;
    $[150] = t43;
    $[151] = t44;
    $[152] = t46;
    $[153] = t47;
    $[154] = t48;
    $[155] = table;
    $[156] = userSettings.resetSettings;
    $[157] = userSettings.retrySave;
    $[158] = userSettings.saveStatus;
    $[159] = t49;
  } else {
    t49 = $[159];
  }
  const contextValue = t49;
  let t50;
  if ($[160] !== children || $[161] !== contextValue) {
    t50 = <TableStateContext.Provider value={contextValue} data-sentry-element="TableStateContext.Provider" data-sentry-component="TableStateProvider" data-sentry-source-file="table-state-provider.tsx">{children}</TableStateContext.Provider>;
    $[160] = children;
    $[161] = contextValue;
    $[162] = t50;
  } else {
    t50 = $[162];
  }
  return t50;
};
const NO_COLUMN_IDS: ReadonlySet<string> = new Set();
const NO_FIELD_IDS: Readonly<Record<string, ReadonlySet<string>>> = {};

/** Columns table settings mark New; none outside a `TableStateProvider`. */
export const useNewColumnIds = () => {
  return useContext(TableStateContext)?.newColumnIds ?? NO_COLUMN_IDS;
};

/**
 * The Column fields table settings mark New, for every column with a Field selection; none outside
 * a `TableStateProvider`.
 */
export const useNewFieldIds = () => {
  return useContext(TableStateContext)?.newFieldIds ?? NO_FIELD_IDS;
};

/**
 * Whether table settings mark something New: a column, or a Column field of a shown column. False
 * outside a `TableStateProvider`.
 */
export const useHasNewSettings = () => {
  return useContext(TableStateContext)?.hasNewSettings ?? false;
};
export function useTableState<TData extends RowData = RowData>(): Omit<ITableStateContext<TData>, 'table'> & TableState<TableFeatures> & {
  table: Table<TData>;
};
export function useTableState<TData extends RowData = RowData, TSelected extends object = TableState<TableFeatures>>(_selector: (_state: TableState<TableFeatures>) => TSelected): Omit<ITableStateContext<TData>, 'table'> & TSelected & {
  table: Table<TData, TSelected>;
};
export function useTableState(selector) {
  const $ = _c(12);
  const providedContext = useContext(TableStateContext);
  if (!providedContext?.table) {
    throw new Error("useTableState must be used within a TableStateProvider");
  }
  const context = providedContext as ITableStateContext<TData>;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      compare: shallow
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const state = useSelector(context.table.store, selector, t0);
  let t1;
  if ($[1] !== context.table.store) {
    t1 = context.table.store.get();
    $[1] = context.table.store;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentState = t1;
  const t2 = context.table;
  const t3 = Boolean(context.bulkActions);
  let t4;
  if ($[3] !== context.table || $[4] !== state || $[5] !== t3) {
    t4 = createTableStateView(t2, state, t3);
    $[3] = context.table;
    $[4] = state;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== context || $[8] !== currentState || $[9] !== state || $[10] !== t4) {
    t5 = {
      ...context,
      ...currentState,
      ...state,
      table: t4
    };
    $[7] = context;
    $[8] = currentState;
    $[9] = state;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp(field_0) {
  return field_0.id;
}
function _temp2(columnId_0) {
  return columnId_0 !== SELECTION_COLUMN_ID;
}
function _temp3(columnId_1) {
  return columnId_1 !== SELECTION_COLUMN_ID;
}
function _temp4(columnId_2) {
  return columnId_2 !== SELECTION_COLUMN_ID;
}
function _temp5(field_1) {
  return field_1.id;
}
function _temp6(t0) {
  const [id, fields] = t0;
  return [id, fields.map(_temp5)];
}
function _temp7(id_0) {
  return id_0 !== SELECTION_COLUMN_ID;
}
function _temp8(id_1) {
  return id_1 !== SELECTION_COLUMN_ID;
}
function _temp9(id_2) {
  return id_2 !== SELECTION_COLUMN_ID;
}
function _temp10(state_0) {
  return {
    columnFilters: state_0.columnFilters,
    sorting: state_0.sorting
  };
}
function _temp11(id_4) {
  return id_4 !== SELECTION_COLUMN_ID;
}
function _temp12(id_5) {
  return id_5 !== SELECTION_COLUMN_ID;
}
function _temp13(id_6) {
  return id_6 !== SELECTION_COLUMN_ID;
}